import { useEffect, useState, useRef, useCallback } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Swal from "sweetalert2"
import { getPaidTickets, getTicket } from "services/tickets"
import { getCustomers, getCustomer } from "services/customers"
import { getCFDIUses } from "services/taxRegimens"
import { getBusinessInfo } from "services/business-info"
import { createTicketInvoice, getTicketInvoices, timbrarTicketInvoice } from "services/ticket-invoices"

export const useBillingForm = (toggle) => {
    const queryClient = useQueryClient()
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [ticketSelectors, setTicketSelectors] = useState([{ id: 1, ticketId: null }])
    const [products, setProducts] = useState([])
    const [ticketDiscounts, setTicketDiscounts] = useState({})

    const getBackendErrorMessage = (error) => {
        if (!error) return "Error al guardar la factura"
        if (error?.errors?.length && error.errors[0]?.message) return error.errors[0].message
        if (typeof error?.detail === "string") return error.detail
        if (typeof error?.message === "string") return error.message
        return "Error al guardar la factura"
    }

    const scrollToTopOnError = () => {
        if (typeof window === "undefined") return

        const performScroll = () => {
            if (typeof document !== "undefined") {
                const errorAlert = document.querySelector(".modal.show .alert-danger")
                if (errorAlert && typeof errorAlert.scrollIntoView === "function") {
                    errorAlert.scrollIntoView({ behavior: "smooth", block: "start" })
                    return
                }

                const modalBody = document.querySelector(".modal.show .modal-body")
                if (modalBody && typeof modalBody.scrollTo === "function") {
                    modalBody.scrollTo({ top: 0, behavior: "smooth" })
                    return
                }

                const modalContent = document.querySelector(".modal.show .modal-content")
                if (modalContent && typeof modalContent.scrollTo === "function") {
                    modalContent.scrollTo({ top: 0, behavior: "smooth" })
                    return
                }
            }

            if (typeof window.scrollTo === "function") {
                window.scrollTo({ top: 0, behavior: "smooth" })
            }
        }

        if (typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    performScroll()
                })
            })
            return
        }

        setTimeout(performScroll, 0)
    }

    const normalizeTaxKey = (tax) => {
        const rawTax = typeof tax === "object"
            ? [tax?.key, tax?.name, tax?.description, tax?.label].filter(Boolean).join(" ")
            : tax
        const normalizedTax = String(rawTax || "").toUpperCase().replace(/\s|%|-/g, "")

        if (!normalizedTax) return null
        if (normalizedTax.includes("IVA")) return "IVA"
        if (normalizedTax.includes("IEPS23") || (normalizedTax.includes("IEPS") && normalizedTax.includes("23"))) {
            return "IEPS23"
        }
        if (normalizedTax.includes("IEPS8") || (normalizedTax.includes("IEPS") && normalizedTax.includes("8"))) {
            return "IEPS8"
        }
        if (normalizedTax.includes("IEPS")) return "IEPS8"

        return null
    }

    const getTaxRateByKey = (taxKey) => {
        const normalizedKey = normalizeTaxKey(taxKey)
        if (normalizedKey === "IVA") return 16
        if (normalizedKey === "IEPS8") return 8
        if (normalizedKey === "IEPS23") return 23
        return 0
    }

    const calculateBreakdownFromGross = (grossTotal, taxKeys) => {
        const normalizedTaxKeys = (taxKeys || [])
            .map(normalizeTaxKey)
            .filter(Boolean)
            .filter((taxKey, index, list) => list.indexOf(taxKey) === index)

        const rates = normalizedTaxKeys
            .map(getTaxRateByKey)
            .filter(rate => rate > 0)

        const taxBreakdown = {
            IVA: 0,
            IEPS8: 0,
            IEPS23: 0,
        }

        if (!rates.length) {
            return {
                subtotal: grossTotal,
                taxes: 0,
                taxBreakdown,
            }
        }

        const totalRate = rates.reduce((acc, rate) => acc + rate, 0)
        if (!totalRate) {
            return {
                subtotal: grossTotal,
                taxes: 0,
                taxBreakdown,
            }
        }

        const subtotalAmount = grossTotal / (1 + (totalRate / 100))
        const taxesAmount = grossTotal - subtotalAmount

        normalizedTaxKeys.forEach((taxKey) => {
            const rate = getTaxRateByKey(taxKey)
            if (rate > 0 && taxBreakdown[taxKey] !== undefined) {
                taxBreakdown[taxKey] += subtotalAmount * (rate / 100)
            }
        })

        return {
            subtotal: subtotalAmount,
            taxes: taxesAmount,
            taxBreakdown,
        }
    }

    // Queries
    const tickets = useQuery(["tickets"], () => getPaidTickets({ per_page: 30, ordering: "-date" }))
    const cfdiUses = useQuery(["cfdi-uses"], () => getCFDIUses())
    const businessInfo = useQuery(["business-info"], () => getBusinessInfo())
    const invoices = useQuery(["invoices"], () => getTicketInvoices({ per_page: 5000 }))

    const loadCustomerOptions = async (inputValue) => {
        const params = { per_page: 30, ordering: "name" }
        if (inputValue && inputValue.trim()) params.search = inputValue.trim()
        const data = await getCustomers(params)
        return (data?.results || []).map(c => ({ value: c.id, label: c.name }))
    }
    const invoicedTicketIds = new Set(
        (invoices.data?.results || [])
            .filter(invoice => (invoice?.invoice_state || "").toLowerCase() !== "cancelada")
            .flatMap(invoice => {
                if (Array.isArray(invoice?.tickets) && invoice.tickets.length > 0) {
                    return invoice.tickets.map(ticket => ticket?.id).filter(Boolean)
                }

                if (Array.isArray(invoice?.ticket_ids) && invoice.ticket_ids.length > 0) {
                    return invoice.ticket_ids.filter(Boolean)
                }

                return invoice?.ticket_id ? [invoice.ticket_id] : []
            })
    )

    const invoicedTicketIdsKey = Array.from(invoicedTicketIds).join("|")

    const debounceRef = useRef({})

    const loadTicketOptions = useCallback(
        async (inputValue, selectorId) => {
            return new Promise((resolve) => {
                const key = `${selectorId}-${inputValue || ""}`
                
                if (debounceRef.current[key]) {
                    clearTimeout(debounceRef.current[key])
                }

                debounceRef.current[key] = setTimeout(async () => {
                    try {
                        // Esperar a que invoices haya cargado para tener datos completos
                        if (!invoices.data) {
                            resolve([])
                            return
                        }

                        const params = { per_page: 30, is_paid: true, ordering: "-date" }
                        if (inputValue && inputValue.trim()) {
                            params.search = inputValue.trim()
                        }

                        const data = await getPaidTickets(params)
                        const selectedByOthers = new Set(
                            ticketSelectors
                                .filter((sel) => sel.id !== selectorId && sel.ticketId)
                                .map((sel) => sel.ticketId)
                        )

                        const options = (data?.results || [])
                            .filter((ticket) => !invoicedTicketIds.has(ticket.id))
                            .filter((ticket) => !selectedByOthers.has(ticket.id))
                            .map((ticket) => ({
                                value: ticket.id,
                                label:
                                    `Ticket #${ticket.folio} - ` +
                                    `${ticket.customer?.name || "Sin cliente"} - ` +
                                    `$${parseFloat(ticket.total).toFixed(2)}`,
                            }))

                        resolve(options)
                    } catch (err) {
                        console.error("Error cargando tickets:", err)
                        resolve([])
                    }
                }, 300)
            })
        },
        [ticketSelectors, invoicedTicketIds, invoices.data]
    )
    useEffect(() => {
        const invoicedTicketIdSet = new Set(invoicedTicketIds)

        setTicketSelectors((prevSelectors) => {
            let changed = false
            const nextSelectors = prevSelectors.map((selector) => {
                if (!selector.ticketId) return selector
                if (!invoicedTicketIdSet.has(selector.ticketId)) return selector
                changed = true
                return { ...selector, ticketId: null, ticketOption: null }
            })

            if (!changed) return prevSelectors
            return nextSelectors
        })

        setProducts((prevProducts) => {
            const filteredProducts = prevProducts.filter((product) => {
                if (!product.ticketId) return true
                return !invoicedTicketIdSet.has(product.ticketId)
            })

            if (filteredProducts.length === prevProducts.length) {
                return prevProducts
            }

            return filteredProducts
        })

        setTicketDiscounts((prev) => {
            const next = { ...prev }
            let changed = false

            Object.keys(next).forEach((ticketId) => {
                if (invoicedTicketIdSet.has(Number(ticketId)) || invoicedTicketIdSet.has(ticketId)) {
                    delete next[ticketId]
                    changed = true
                }
            })

            return changed ? next : prev
        })
    }, [invoicedTicketIdsKey])

    const usoCfdiOptions = cfdiUses.data?.results?.map(u => ({
        value: u.code,
        label: `${u.code} - ${u.description}`
    })) || []

    const summary = products.reduce((acc, product) => {
        const gross = Number(product.total || 0)
        const breakdown = calculateBreakdownFromGross(gross, product.taxKeys)
        return {
            subtotal: acc.subtotal + breakdown.subtotal,
            taxesAmount: acc.taxesAmount + breakdown.taxes,
            total: acc.total + gross,
            taxBreakdown: {
                IVA: acc.taxBreakdown.IVA + (breakdown.taxBreakdown.IVA || 0),
                IEPS8: acc.taxBreakdown.IEPS8 + (breakdown.taxBreakdown.IEPS8 || 0),
                IEPS23: acc.taxBreakdown.IEPS23 + (breakdown.taxBreakdown.IEPS23 || 0),
            },
        }
    }, {
        subtotal: 0,
        taxesAmount: 0,
        total: 0,
        taxBreakdown: { IVA: 0, IEPS8: 0, IEPS23: 0 }
    })

    const subtotal = summary.subtotal
    const taxesAmount = summary.taxesAmount
    const totalAmount = summary.total
    const taxBreakdown = summary.taxBreakdown
    const ivaAmount = taxBreakdown.IVA || 0
    const iepsAmount = (taxBreakdown.IEPS8 || 0) + (taxBreakdown.IEPS23 || 0)
    const iepsPercent = taxBreakdown.IEPS23 > 0 && taxBreakdown.IEPS8 > 0
        ? 0
        : taxBreakdown.IEPS23 > 0
        ? 23
        : taxBreakdown.IEPS8 > 0
        ? 8
        : 0

    const selectedTicketIds = ticketSelectors
        .filter((selector) => selector.ticketId)
        .map((selector) => selector.ticketId)

    const selectedTicketIdSet = new Set(selectedTicketIds.map((id) => String(id)))
    const discountAmount = Object.entries(ticketDiscounts).reduce((acc, [ticketId, discount]) => {
        if (!selectedTicketIdSet.has(ticketId)) return acc
        return acc + Number(discount || 0)
    }, 0)

    const finalTotalAmount = Math.max(totalAmount - discountAmount, 0)

    const initialValues = {
        client: "", clientName: "", date: new Date().toLocaleDateString("es-MX"),
        companyName: "", paymentForm: "", rfc: "", paymentMethod: "",
        usoCfdi: "", fiscalRegimen: "", postalCode: "",
        emitterRfc: "", emitterCompanyName: "", emitterFiscalRegimen: "", emitterPostalCode: "",
        selectedTicket: ""
    }

    // Handlers
    const handleTicketChange = async (selectorId, selectedTicketOption) => {
        const selectedTicketId = selectedTicketOption?.value || null
        
        const currentSelector = ticketSelectors.find(s => s.id === selectorId)
        const previousTicketId = currentSelector?.ticketId

        if (!selectedTicketId) {
            setTicketSelectors((prev) => prev.map((s) => (
                s.id === selectorId ? { ...s, ticketId: null, ticketOption: null } : s
            )))

            if (previousTicketId) {
                setProducts((prev) => prev.filter((p) => p.ticketId !== previousTicketId))
                setTicketDiscounts((prev) => {
                    const next = { ...prev }
                    delete next[String(previousTicketId)]
                    return next
                })
            }
            return
        }
        
        const alreadySelected = ticketSelectors.some(
            s => s.id !== selectorId && s.ticketId === selectedTicketId
        )
        
        if (alreadySelected) {
            Swal.fire({ icon: "warning", title: "Ticket ya agregado" })
            return
        }
        
        try {
            setIsLoading(true)
            const ticketData = await getTicket(selectedTicketId)
            
            if (!ticketData?.products?.length) {
                Swal.fire({ icon: "warning", title: "Sin productos" })
                return
            }
            
            const newProducts = ticketData.products.map(item => {
                const productTaxes = item.product?.product?.taxes || []
                const normalizedTaxKeys = productTaxes
                    .map(normalizeTaxKey)
                    .filter(Boolean)
                    .filter((taxKey, index, list) => list.indexOf(taxKey) === index)
                const taxesData = {
                    keys: normalizedTaxKeys,
                    labels: normalizedTaxKeys.join(", ") || "NO",
                }

                return ({
                ticketId: ticketData.id, ticketFolio: ticketData.folio, productId: item.id,
                sourceProductId: item.product?.product?.id,
                description: item.product?.product?.name || item.product?.description 
                    || item.description || "Sin descripción",
                unitSat: `${item.unit_measurement?.key}-${item.unit_measurement?.name}` || "Indefinido",
                satCode: item.product?.product?.sat_code || item.product?.sat_code || "Indefinido",
                taxKeys: taxesData.keys,
                taxLabel: taxesData.labels,
                quantity: item.quantity || 0, unitPrice: parseFloat(item.price || 0), 
                total: parseFloat(item.total || 0)
            })
            })
            
            setTicketSelectors(prev => prev.map(s => 
                s.id === selectorId
                    ? { ...s, ticketId: selectedTicketId, ticketOption: selectedTicketOption }
                    : s
            ))

            setTicketDiscounts((prev) => ({
                ...prev,
                [String(ticketData.id)]: Number(ticketData.discount || 0),
            }))

            if (previousTicketId && previousTicketId !== ticketData.id) {
                setTicketDiscounts((prev) => {
                    const next = { ...prev }
                    delete next[String(previousTicketId)]
                    return next
                })
            }
            
            const updatedProducts = previousTicketId 
                ? products.filter(p => p.ticketId !== previousTicketId)
                : products
            
            const finalProducts = [...updatedProducts, ...newProducts]
            setProducts(finalProducts)
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: "No se pudo obtener la información del ticket" })
        } finally {
            setIsLoading(false)
        }
    }
    
    const handleAddSelector = () => {
        const newId = Math.max(...ticketSelectors.map(s => s.id), 0) + 1
        setTicketSelectors(prev => [...prev, { id: newId, ticketId: null, ticketOption: null }])
    }
    
    const handleRemoveSelector = (selectorId) => {
        const selector = ticketSelectors.find(s => s.id === selectorId)
        
        if (selector?.ticketId) {
            const updatedProducts = products.filter(p => p.ticketId !== selector.ticketId)
            setProducts(updatedProducts)
            setTicketDiscounts((prev) => {
                const next = { ...prev }
                delete next[String(selector.ticketId)]
                return next
            })
        }
        
        if (ticketSelectors.length > 1) {
            setTicketSelectors(prev => prev.filter(s => s.id !== selectorId))
        } else {
            setTicketSelectors([{ id: 1, ticketId: null, ticketOption: null }])
        }
    }

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setIsLoading(true)
            setError(null)
            
            // Validar que haya tickets seleccionados
            const selectedTickets = selectedTicketIds
            if (!selectedTickets.length) {
                throw new Error("Debe seleccionar al menos un ticket")
            }
            
            // Normalizar tax_regimen a solo 3 caracteres
            const normalizedTaxRegimen = values.fiscalRegimen 
                ? values.fiscalRegimen.substring(0, 3).trim() 
                : ""
            
            // Normalizar emitter_tax_regimen a solo 3 caracteres
            const normalizedEmitterTaxRegimen = values.emitterFiscalRegimen 
                ? values.emitterFiscalRegimen.substring(0, 3).trim() 
                : ""
            
            // Convertir invoice_date de formato local a ISO 8601
            // Si es "22/2/2026" convertir a "2026-02-22T00:00:00Z"
            const parseDateToISO = (dateStr) => {
                if (!dateStr) return new Date().toISOString()
                
                // Si ya está en ISO format, devolverlo
                if (dateStr.includes("T")) return dateStr
                
                // Parsear formato local (dd/m/yyyy o d/m/yyyy)
                const parts = dateStr.split("/")
                if (parts.length === 3) {
                    const [day, month, year] = parts
                    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                    return date.toISOString()
                }
                
                return new Date().toISOString()
            }
            
            // Payload de la factura según lo que espera el backend
            const billingProducts = products.map(product => {
                const productTaxBreakdown = calculateBreakdownFromGross(Number(product.total || 0), product.taxKeys)
                const productIvaAmount = productTaxBreakdown.taxBreakdown.IVA || 0
                const productIepsAmount = (productTaxBreakdown.taxBreakdown.IEPS8 || 0) +
                    (productTaxBreakdown.taxBreakdown.IEPS23 || 0)
                const productIepsPercent =
                    productTaxBreakdown.taxBreakdown.IEPS23 > 0 && productTaxBreakdown.taxBreakdown.IEPS8 > 0
                        ? 0
                        : productTaxBreakdown.taxBreakdown.IEPS23 > 0
                        ? 23
                        : productTaxBreakdown.taxBreakdown.IEPS8 > 0
                        ? 8
                        : 0

                return {
                    product_id: product.productId,
                    description: product.description,
                    quantity: product.quantity,
                    unit_price: product.unitPrice,
                    total: product.total,
                    unit_sat: product.unitSat ? product.unitSat.substring(0, 3).trim() : "H87",
                    sat_code: product.satCode ? product.satCode.substring(0, 8).trim() : "01010101",
                    tax_keys: product.taxKeys || [],
                    tax_label: product.taxLabel || "NO",
                    iva_percent: productIvaAmount > 0 ? 16 : 0,
                    iva_amount: parseFloat(productIvaAmount.toFixed(2)),
                    ieps_percent: productIepsPercent,
                    ieps_amount: parseFloat(productIepsAmount.toFixed(2)),
                }
            })

            const billingPayload = {
                // Información de los tickets a facturar
                ticket_ids: selectedTickets,
                ticket_id: selectedTickets[0],
                
                // Datos del cliente
                customer_id: values.client,
                
                // Información fiscal del receptor
                business_name: values.companyName,
                rfc: values.rfc,
                postal_code: values.postalCode,
                tax_regimen: normalizedTaxRegimen,
                cfdi_use: values.usoCfdi.substring(0, 4),
                
                // Información del emisor
                emitter_company_name: values.emitterCompanyName,
                emitter_rfc: values.emitterRfc,
                emitter_tax_regimen: normalizedEmitterTaxRegimen,
                emitter_postal_code: values.emitterPostalCode,
                
                // Información de pago
                payment_form: values.paymentForm,
                payment_method: values.paymentMethod,
                
                // Datos de los productos
                products: billingProducts,
                
                // Totales
                subtotal: parseFloat(subtotal.toFixed(2)),
                iva_percent: ivaAmount > 0 ? 16 : 0,
                iva_amount: parseFloat(ivaAmount.toFixed(2)),
                ieps_percent: iepsPercent,
                ieps_amount: parseFloat(iepsAmount.toFixed(2)),
                tax: parseFloat(taxesAmount.toFixed(2)),
                total: parseFloat(finalTotalAmount.toFixed(2)),
                
                // Fecha - convertir a ISO 8601
                invoice_date: parseDateToISO(values.date)
            }
            
            // console.log de los datos a enviar al backend
            // console.log("Payload de la factura:", billingPayload)
            
            // Confirmar envío a timbrado
            const confirmResult = await Swal.fire({
                title: "¿Timbrar factura?",
                text: "Al aceptar se timbrará esta factura.",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Aceptar",
                cancelButtonText: "Cancelar",
            })

            if (!confirmResult.isConfirmed) {
                // Usuario canceló el envío a timbrado, no crear la factura
                setIsLoading(false)
                setSubmitting(false)
                return
            }

            // Enviar billingPayload al backend
            const response = await createTicketInvoice(billingPayload)
            // console.log("Factura creada:", response)

            const invoiceId = response?.data?.id || response?.data?.results?.id || response?.id || null

            // Intentar timbrar inmediatamente
            try {
                if (!invoiceId) {
                    throw new Error("No se obtuvo el id de la factura creada")
                }
                await timbrarTicketInvoice(invoiceId)

                await queryClient.invalidateQueries({ queryKey: ["invoices"] })
                await Swal.fire({ title: "¡Factura timbrada!", icon: "success" })
                toggle()
            } catch (timbrarError) {
                // Parsear mensaje de error devuelto por la API
                const apiError = timbrarError?.response?.data || timbrarError
                const message = getBackendErrorMessage(apiError)

                // Mostrar un solo modal y desplegar detalles dentro del mismo
                await Swal.fire({
                    title: "No se pudo timbrar esta factura",
                    icon: "error",
                    confirmButtonText: "Aceptar",
                    html: `
                        <p style="margin: 0 0 10px 0;">Ocurrió un error al timbrar la factura.</p>
                        <button id="swal-toggle-error-detail" type="button" class="swal2-styled" 
                            style="margin: 0 0 10px 0;">Ver detalles</button>
                        <div id="swal-error-detail-box" style="display:block; max-height:0; opacity:0; 
                            overflow:hidden; margin-top:0; transition:max-height 260ms ease, opacity 220ms ease, 
                            margin-top 260ms ease;">
                            <textarea readonly style="width:100%;height:200px;">${message}</textarea>
                        </div>
                    `,
                    didOpen: () => {
                        const toggleBtn = document.getElementById("swal-toggle-error-detail")
                        const detailBox = document.getElementById("swal-error-detail-box")

                        if (toggleBtn && detailBox) {
                            toggleBtn.dataset.expanded = "false"
                            toggleBtn.addEventListener("click", () => {
                                const isExpanded = toggleBtn.dataset.expanded === "true"

                                if (!isExpanded) {
                                    // Expandir con animación suave
                                    detailBox.style.marginTop = "8px"
                                    detailBox.style.opacity = "1"
                                    detailBox.style.maxHeight = `${detailBox.scrollHeight}px`
                                    toggleBtn.textContent = "Ocultar detalles"
                                    toggleBtn.dataset.expanded = "true"
                                } else {
                                    // Contraer con animación suave
                                    detailBox.style.maxHeight = "0px"
                                    detailBox.style.opacity = "0"
                                    detailBox.style.marginTop = "0"
                                    toggleBtn.textContent = "Ver detalles"
                                    toggleBtn.dataset.expanded = "false"
                                }
                            })
                        }
                    },
                })

                // Mantener el formulario abierto para que el usuario pueda revisar/editar
                await queryClient.invalidateQueries({ queryKey: ["invoices"] })
                await queryClient.invalidateQueries({ queryKey: ["tickets"] })
            }
        } catch (error) {
            setError(getBackendErrorMessage(error))
            scrollToTopOnError()
            await queryClient.invalidateQueries({ queryKey: ["invoices"] })
            await queryClient.invalidateQueries({ queryKey: ["tickets"] })
        } finally {
            setIsLoading(false)
            setSubmitting(false)
        }
    }

    const handleClientChange = async (clientId, clientName, setFieldValue) => {
        setFieldValue("client", clientId)
        setFieldValue("clientName", clientName || "")
        
        if (!clientId) {
            ["companyName", "rfc", "postalCode", "usoCfdi", "fiscalRegimen", "clientName"]
                .forEach(f => setFieldValue(f, ""))
            return
        }
        
        try {
            const client = await getCustomer(clientId)
            if (client) {
                setFieldValue("companyName", client.business_name || client.name || "")
                setFieldValue("rfc", client.rfc || "")
                setFieldValue("postalCode", client.postal_code || "")
                const cfdiText = client.cfdi_use ? client.cfdi_use.code : ""
                const regimenText = client.tax_regimen 
                    ? `${client.tax_regimen.code} - ${client.tax_regimen.name}` : ""
                setFieldValue("usoCfdi", cfdiText)
                setFieldValue("fiscalRegimen", regimenText)
            }
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: "No se pudo obtener la información del cliente" })
        }
    }

    return {
        // State
        error,
        isLoading,
        ticketSelectors,
        products,
        subtotal,
        discount: discountAmount,
        taxesAmount,
        taxBreakdown,
        total: finalTotalAmount,
        initialValues,
        
        // Queries
        tickets,
        cfdiUses,
        businessInfo,
        invoices,
        
        // Computed
        loadCustomerOptions,
        loadTicketOptions,
        usoCfdiOptions,
        
        // Handlers
        handleTicketChange,
        handleAddSelector,
        handleRemoveSelector,
        handleSubmit,
        handleClientChange
    }
}
